/**
 * What Toado's own pages call their screens, for in-page pins (TOADO-701).
 *
 * Several screens of the app share one address: the tabs of project settings,
 * the board's views, a ticket's debugging tabs. Pins are found by address, so
 * the extension tells those screens apart by the `data-toado-view` nearest the
 * pinned element, and shows a pin only while its name is on screen. A region
 * marked `*` (`EVERY_SCREEN`) is on every screen, and so are its pins.
 *
 * A page names each of its screens `<page>/<screen>` on the region that
 * switches, and marks `*` on what its screens share. Where a page is mostly
 * shared, the `*` goes on its root: the nearest name wins, so only the named
 * regions inside it belong to one screen. A page that can also sit inside
 * another page's screen (a ticket does, in the board's single view) names its
 * root `<page>` instead, which keeps it on all of its own screens and off the
 * other page's.
 *
 * A name is stored with every pin placed under it, so a name never changes
 * once shipped: renaming one leaves its pins on "another screen" for good.
 * The extension lowercases names and drops their digits before comparing, so
 * every name here is lowercase with no digits, which keeps the stored value
 * the same as the one written here.
 */

/** A region that is part of every screen of its page. */
export const EVERY_SCREEN = '*';

/** Project settings: each tab is `project-settings/<tab>`. */
export const PROJECT_SETTINGS_SCREEN = 'project-settings';

/**
 * The four widget tabs of project settings share this name, because they
 * share one editor with its own header, preview and save bar. Each section,
 * and each group inside Appearance and Capture, is named below it.
 */
export const WIDGET_SETTINGS_SCREEN = `${PROJECT_SETTINGS_SCREEN}/widget`;

/** The widget setup wizard's steps: `widget-setup/<step>`. */
export const WIDGET_SETUP_SCREEN = 'widget-setup';

/** A project's board, one screen per view: `project-board/<view>`. */
export const PROJECT_BOARD_SCREEN = 'project-board';

/** A ticket, and its debugging tabs: `ticket/devtools/<tab>`. */
export const TICKET_SCREEN = 'ticket';

/** The inbox, archive and drafts lists, one screen per view: `<list>/<view>`. */
export const INBOX_SCREEN = 'inbox';
export const ARCHIVE_SCREEN = 'archive';
export const DRAFTS_SCREEN = 'drafts';

/** The MCP setup walk-through, one screen per client: `mcp-setup/<client>`. */
export const MCP_SETUP_SCREEN = 'mcp-setup';

/** Onboarding's steps: `onboarding/<step>`. */
export const ONBOARDING_SCREEN = 'onboarding';

/** Admin usage, one screen per tab: `admin-usage/<tab>`. */
export const ADMIN_USAGE_SCREEN = 'admin-usage';
